import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import omit from 'lodash/omit';
import last from 'lodash/last';

import WpToastManager from 'Wpds/components/toast/WpToastManager';
import TextField from 'Shared/components/TextField/TextField';
import PrepopulatedTextField from 'Shared/components/PrepopulatedTextField/PrepopulatedTextField';
import { isEmptyString, defaultTeammateMessage, validEmail } from 'Shared/helpers';
import { bulkCreateWorkspaces } from 'Shared/actions/workspaces';
import { WorkspaceType } from 'Shared/constants/types';
import { getOrganizationDomain } from 'Shared/selectors/organization';
import { useQueryParam } from 'Shared/utils/hooks';
import QueryParamConstants from 'Shared/constants/QueryParams.constants';

import SimpleAddWorkspaceWrapper from '../SimpleAddWorkspaceWrapper/SimpleAddWorkspaceWrapper';
import './SimpleBulkInviteTeammatesModal.scss';
import { Illustrations } from 'Wpds/components/illustration';
import { WpIcon } from 'Wpds/components/icon';

const emptyFormValue = domain => ({ fullName: '', email: domain, workspaceId: null });

function SimpleBulkInviteTeammatesModal() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [modalParam, setModalParam] = useQueryParam(QueryParamConstants.MULTIPLE_INVITE_MODAL);
  const isCalendarConnected = useSelector(state => state.data_sources.find(ds => ds.isCalendar && ds.connected));
  const organizationDomain = useSelector(getOrganizationDomain);
  const notInvitedTeammates = useSelector(state =>
    state.workspaces
      .filter(
        ({ isPersonal, isGroup, users }) => !isPersonal && !isGroup && !users.find(({ id }) => id !== state.user.id)
      )
      .map(({ id, name }) => ({ workspaceId: id, fullName: name, email: organizationDomain }))
  );
  const [formValues, setFormValues] = useState(
    notInvitedTeammates.length ? [...notInvitedTeammates] : [emptyFormValue(organizationDomain)]
  );
  const [personalMessage, setPersonalMessage] = useState(defaultTeammateMessage);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const checkInvalidFields = ({ fullName, email }) => {
    if (isEmptyString(fullName) && (isEmptyString(email) || email === organizationDomain)) return null;
    if (isEmptyString(email)) return 'Please enter an email address';
    if (!validEmail(email) && email.startsWith('@')) return `Incomplete email address: ${email}`;
    if (!validEmail(email)) return `Invalid email address: ${email}`;
    if (isEmptyString(fullName)) return 'Please enter a name';
    return null;
  };

  const handleSubmit = async () => {
    const checkedformValues = Object.values(formValues).map(formValue => ({
      ...formValue,
      errorMessage: checkInvalidFields(formValue),
    }));
    if (checkedformValues.find(({ errorMessage }) => errorMessage)) {
      setFormValues(checkedformValues);
      return null;
    }
    const valuesWithoutEmptyEmails = checkedformValues.filter(
      ({ email }) => !isEmptyString(email) && email !== organizationDomain
    );

    try {
      setIsSubmitting(true);
      const workspacesParams = valuesWithoutEmptyEmails.map(values => ({
        name: values.fullName,
        inviteeEmails: [values.email],
        personalMessage,
        workspaceId: values.workspaceId,
      }));
      const firstCreatedWorkspace = await dispatch(bulkCreateWorkspaces({ workspacesParams }));
      if (firstCreatedWorkspace?.path) history.push(firstCreatedWorkspace?.path);
      else setIsSubmitting(false);
    } catch (e) {
      WpToastManager.danger('Something went wrong!');
      setIsSubmitting(false);
    }
    return null;
  };

  const handleBlur = key => {
    if (formValues[key].errorMessage)
      setFormValues({
        ...formValues,
        [key]: { ...formValues[key], errorMessage: checkInvalidFields(formValues[key]) },
      });
  };

  const handleTextInputChange = (event, key) =>
    setFormValues({ ...formValues, [key]: { ...formValues[key], [event.target.name]: event.target.value } });

  const handlePersonalMessageChange = event => setPersonalMessage(event.target.value);

  const handleRemoveItem = key => setFormValues(omit(formValues, [key]));

  const handleNewItemAddClick = () => {
    const lastKey = last(Object.keys(formValues));
    const newKey = parseInt(lastKey, 10) + 1;
    setFormValues({ ...formValues, [newKey]: emptyFormValue(organizationDomain) });
  };

  const valuesCount = Object.values(formValues).length;
  const isAllValuesEmpty = Object.values(formValues).every(
    ({ email }) => isEmptyString(email) || email === organizationDomain
  );
  const isSubmitDisabled = isAllValuesEmpty || isSubmitting;

  if (!modalParam) return null;

  return (
    <SimpleAddWorkspaceWrapper
      onClose={() => setModalParam(null)}
      workspaceType={WorkspaceType.isTeammate}
      title="Invite Teammates"
      description="Invite your teammates via email"
      submitButtonText="Invite Teammate(s)"
      disabledSubmit={isSubmitDisabled}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      createNameSection={
        <>
          <div className="mt-3">
            {Object.entries(formValues).map((formValue, index) => (
              <div key={formValue[0]}>
                <div className="d-flex">
                  <TextField
                    name="fullName"
                    type="text"
                    label={index === 0 ? 'Name' : null}
                    labelMuted={false}
                    placeholder="Enter Name"
                    wrapperClassName="mb-2 mr-2"
                    className="bulk-invite-temmates-modal-input py-1"
                    value={formValue[1].fullName}
                    onChange={e => handleTextInputChange(e, formValue[0])}
                    onBlur={() => handleBlur(formValue[0])}
                  />
                  <PrepopulatedTextField
                    name="email"
                    type="text"
                    label={index === 0 ? 'Email' : null}
                    labelMuted={false}
                    placeholder="email"
                    prepopulatedText={organizationDomain}
                    wrapperClassName="mb-2"
                    className="bulk-invite-temmates-modal-input py-1"
                    value={formValue[1].email}
                    onChange={e => handleTextInputChange(e, formValue[0])}
                    onBlur={() => handleBlur(formValue[0])}
                  />
                  {valuesCount > 1 ? (
                    <span
                      className={`bulk-invite-temmates-modal-delete-icon align-self-center ml-2 ${
                        index === 0 ? 'mt-3' : 'mb-1'
                      }`}
                      onClick={() => handleRemoveItem(formValue[0])}
                    >
                      <WpIcon prefix="fas" name="times-circle" />
                    </span>
                  ) : null}
                </div>
                {formValue[1].errorMessage && (
                  <div className="alert alert-danger p-2 mb-2 small w-100" role="alert">
                    {formValue[1].errorMessage}
                  </div>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={handleNewItemAddClick}
            className="bulk-invite-temmates-modal-button btn btn-link text-decoration-none p-0 mb-3"
          >
            <span className="mr-1">
              <WpIcon name="plus-circle" />
            </span>
            Add another
          </button>
          {!isSubmitDisabled ? (
            <div className="d-flex flex-column mt-2">
              <b>Invite message</b>
              <textarea
                name="personalMessage"
                className="w-100 border rounded p-2 mt-2"
                value={personalMessage}
                onChange={handlePersonalMessageChange}
              />
            </div>
          ) : null}
        </>
      }
      footerSection={
        isSubmitDisabled && !isCalendarConnected ? (
          <div className="bulk-invite-temmates-modal-pro-tip px-3 py-2 mt-4">
            Pro tip: <a href="/user/integrations">Connect your calendar</a> to quickly invite & sync workspaces for any
            of your recurring meetings
          </div>
        ) : null
      }
      illustration={Illustrations.two_people_standing_with_laptop_purple_bg}
    />
  );
}

export default SimpleBulkInviteTeammatesModal;
